import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ButtonEC.module.css";
import { useBoundStore } from "@maxis/client-logic/store/useBoundStore";
import useMessageBox from "@maxis/client-logic/hooks/useMessageBox";

/**
 * Button
 * @type React.FC<ButtonPropTypes>
 */

const Button = React.forwardRef((props, ref) => {
  const { alert } = useMessageBox();
  const menuId = useBoundStore((state) => state.menu.selectedMenu.menuId);
  const authBtnList = useBoundStore((state) => state.authBtn.authBtnList);

  Object.keys(props).forEach((p) => {
    if (!Object.keys(ButtonPropTypes).includes(p)) {
      console.error(`Button: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /**
   * Button Component의 State
   */
  const [currState, setCurrState] = useState({
    label: props.label,
    buttonType: props.buttonType,
    colorType: props.colorType,
    size: props.size,
    iconType: props.iconType,
    disabled: props.disabled,
  });

  const _updateState = (stateName, newValue) => {
    // currState에 있는 값만 업데이트 가능
    if (Object.keys(currState).includes(stateName)) {
      setCurrState((prev) => ({
        ...prev,
        [stateName]: newValue,
      }));
    } else {
      console.error("State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다.");
    }
  };

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    refButton,
    updateState(stateName, newValue) {
      _updateState(stateName, newValue);
    },

    runClick() {
      props.onClick();
    },

    enable() {
      _updateState("disabled", false);
    },

    disable() {
      _updateState("disabled", true);
    },
  }));

  const refButton = useRef();

  const _makeClassnames = ({ buttonType, colorType, size, iconType }) => {
    const classNames = [];
    classNames.push(styles.btn);

    switch (buttonType) {
      case "default":
        break;
      case "close":
        classNames.push(styles.closeBtn);
        break;
      case "search":
        classNames.push(styles.searchBtn);
        break;
    }

    if (buttonType === "default") {
      switch (colorType) {
        case "default":
          classNames.push(styles.colorDefault);
          break;
        case "default-outline":
          classNames.push(styles.colorDefaultOutline);
          break;
        case "main":
          classNames.push(styles.colorMain);
          break;
        case "main-outline":
          classNames.push(styles.colorMainOutline);
          break;
        case "sub1":
          classNames.push(styles.colorSub1);
          break;
        case "sub1-outline":
          classNames.push(styles.colorSub1Outline);
          break;
        case "sub2":
          classNames.push(styles.colorSub2);
          break;
        case "sub2-outline":
          classNames.push(styles.colorSub2Outline);
          break;
      }
    }

    switch (size) {
      case "default":
        break;
      case "small":
        classNames.push(styles.small);
        break;
      case "middle":
        classNames.push(styles.middle);
        break;
      case "large":
        classNames.push(styles.large);
        break;
    }

    if (iconType) {
      classNames.push(styles.icon);
      classNames.push(styles[iconType]);
    }

    return classNames.join(" ");
  };

  const _onClick = () => {
    if (!props.onClick) {
      alert("확인", "지정된 클릭 이벤트가 없습니다.");
      return;
    }

    if (!props.checkAuth) {
      props.onClick();
      return;
    }

    const authBtn = authBtnList?.filter((x) => x.menuId === menuId)?.filter((x) => x.btnType === props.authBtn);

    if (authBtn.length) {
      props.onClick();
    } else {
      alert("확인", "버튼 권한이 없습니다.");
    }
  };

  return (
    <button
      ref={refButton}
      type="button"
      className={`${_makeClassnames(currState)}`}
      onClick={_onClick}
      disabled={currState.disabled}
    >
      <span>{currState.label}</span>
    </button>
  );
});

const ButtonPropTypes = {
  /**
   * label: 버튼 텍스트
   * updateState('label', string) 으로 수정 가능
   */
  label: PropTypes.string,
  /**
   * buttonType: default, close, search 중 한가지
   * updateState('buttonType', string) 으로 수정 가능
   */
  buttonType: PropTypes.oneOf(["default", "close", "search"]),
  /**
   * colorType: default, main, sub1, sub2 중 한가지
   * updateState('colorType', string) 으로 수정 가능
   */
  colorType: PropTypes.oneOf([
    "default",
    "main",
    "sub1",
    "sub2",
    "default-outline",
    "main-outline",
    "sub1-outline",
    "sub2-outline",
  ]),
  /**
   * size: default, small, middle, large 중 한가지
   * updateState('size', string) 으로 수정 가능
   */
  size: PropTypes.oneOf(["default", "small", "middle", "large"]),
  /**
   * iconType: 아이콘. Button.module.css 에서 가용한 아이콘 참고
   * updateState('iconType', string) 으로 수정 가능
   */
  iconType: PropTypes.string,
  /**
   * onClick: 클릭 이벤트
   */
  onClick: PropTypes.func,
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,

  /**
   * authBtn: 버튼 권한
   */
  authBtn: PropTypes.string,
  /**
   * checkAuth: 버튼 권한 사용 여부
   */
  checkAuth: PropTypes.bool,
};

Button.propTypes = ButtonPropTypes;
Button.defaultProps = {
  label: "버튼",
  buttonType: "default",
  colorType: "default",
  size: "default",
  iconType: null,
  onClick: null,
  disabled: false,
  authBtn: "noneBtn",
  checkAuth: false,
};
Button.displayName = "Button";
export default Button;
